import { Button } from '../catalyst/button'
import { Field as FormField, Label } from '../catalyst/fieldset'
import { useLogNames } from '../lib/farm-log-names'
import { rememberPeople, usePeople } from '../lib/people'
import { PersonNameInput } from '../lib/person-picker'
import { fieldIdsOf, fieldValues, patchField, str, type StepCtx } from './flow'

/**
 * "Who applied it?" from the saved people list. With several fields, one person for all of them,
 * and a per-field override for the fields someone else did (kept in `values.perField`).
 */
export function WhoField({ ctx, label = 'Who applied it?', valueKey = 'applicator' }: { ctx: StepCtx; label?: string; valueKey?: string }) {
  const { orgId, farmId, values, patch, selectedFields } = ctx
  const { people } = usePeople(orgId)
  const { names } = useLogNames(orgId, farmId, 'applicator')
  const perField = values.whoPerField === true && selectedFields.length > 1
  const shared = str(values[valueKey])

  return (
    <div className="space-y-4">
      <FormField>
        <Label>{perField ? `${label} (most fields)` : label}</Label>
        <PersonNameInput people={people} pastNames={names} value={shared} onChange={(name) => patch({ [valueKey]: name })} />
      </FormField>
      {selectedFields.length > 1 &&
        (perField ? (
          <div className="space-y-4 rounded-lg p-4 ring-1 ring-zinc-950/10 dark:ring-white/10">
            {selectedFields.map((f) => (
              <FormField key={f.id}>
                <Label>{f.name}</Label>
                <PersonNameInput
                  people={people}
                  pastNames={names}
                  placeholder={shared || undefined}
                  value={str(fieldValues(values, f.id)[valueKey])}
                  onChange={(name) => patchField(ctx, f.id, { [valueKey]: name || undefined })}
                />
              </FormField>
            ))}
            <Button
              plain
              onClick={() =>
                patch({
                  whoPerField: false,
                  perField: Object.fromEntries(Object.entries((values.perField as Record<string, object>) ?? {}).map(([id, v]) => [id, { ...v, [valueKey]: undefined }])),
                })
              }
            >
              Same person for every field
            </Button>
          </div>
        ) : (
          <Button plain onClick={() => patch({ whoPerField: true })}>
            Different person for some fields
          </Button>
        ))}
    </div>
  )
}

/** Every name the entry used for `valueKey`, shared and per field. */
export function namesUsed(values: StepCtx['values'], valueKey = 'applicator'): string[] {
  return [str(values[valueKey]), ...fieldIdsOf(values).map((id) => str(fieldValues(values, id)[valueKey]))].filter((n) => n.trim() !== '')
}

/** For a flow's `onRecorded`: puts the names the entry used on the saved people list. */
export const rememberWho =
  (...valueKeys: string[]) =>
  (ctx: StepCtx) =>
    rememberPeople(ctx.orgId, (valueKeys.length ? valueKeys : ['applicator']).flatMap((k) => namesUsed(ctx.values, k)))
